import ReservationInfo from './ReservationInfo';
import { useEffect, useState } from 'react';
import { useLang } from '../hooks/useLanguage';

export function ReservationContactInfo({ reservation }: { reservation: any }) {
    const { getI18n, t } = useLang();
    const [uniqueCustomFields, setUniqueCustomFields] = useState<any[]>([]);

    useEffect(() => {
        if (!reservation) {
            return;
        }
        const bookingNote = reservation?.note || [];

        // extract unique custom fields from booking items
        const customFields = reservation?.cart
            ?.map((item: any) => item.ticketTypeCustomFields)
            .flat(1);

        // deduplicate custom fields by name, type, and description, but keep the one with required=true if any
        const allCustomFields = customFields || [];
        const deduplicatedCustomFields: any[] = [];
        allCustomFields.forEach((field: any) => {
            const existingIndex = deduplicatedCustomFields.findIndex(
                (f) =>
                    f.name === field.name &&
                    f.type === field.type &&
                    // TODO: should check everything? (description, description_i18n and name_i18n)
                    JSON.stringify(f.description) === JSON.stringify(field.description),
            );
            if (existingIndex === -1) {
                deduplicatedCustomFields.push(field);
            } else if (field.required && !deduplicatedCustomFields[existingIndex].required) {
                deduplicatedCustomFields[existingIndex] = field;
            }
        });
        const uniqueCustomFields = [
            ...new Set(deduplicatedCustomFields.map((field: any) => JSON.stringify(field))),
        ]
            .map((field) => JSON.parse(field as string))
            .map((e) => {
                // extend with values
                const n = bookingNote.find((note: any) => note.label === e.name);
                return {
                    ...e,
                    ticketField: true,
                    oldNote: true,
                    value: n ? n.value : '',
                };
            });

        // combine unique custom fields with bookingNote fields
        const combinedCustomFields = [
            ...uniqueCustomFields,
            ...bookingNote
                // filter out custom fields that are already in bookingNote
                .filter(
                    (note: any) => !uniqueCustomFields.some((field) => field.name === note.label),
                )
                // these are the ones added as random notes, so these are the chatty ones
                .map((note: any) => ({
                    user: reservation.noteAgents.find((agent: any) => agent.id === note.user),
                    name: note.label,
                    type: 'chat',
                    value: note.value,
                    ticketField: false,
                    oldNote: true,
                    required: false,
                })),
        ];

        setUniqueCustomFields(combinedCustomFields);
    }, [reservation]);

    return (
        <div className="fireknife-contact-and-other-data">
            <h2 className="fireknife-contact-and-other-data__title">
                {t('widgetReservationInfo.contactInformationAndOtherData')}
            </h2>

            <div className="fireknife-contact-and-other-data__boxes">
                <div className="fireknife-contact-and-other-data__box fireknife-contact-and-other-data__box--1">
                    <ReservationInfo reservation={reservation} />
                </div>
                <div className="fireknife-contact-and-other-data__box fireknife-contact-and-other-data__box--2">
                    {reservation.note && reservation.note.length > 0 && (
                        <div className="fireknife-booking__ticket-notes">
                            <dl className="fireknife-reservation-info__dl">
                                {uniqueCustomFields
                                    .filter((field) => field.type !== 'chat')
                                    .map((field, idx) => (
                                        <div key={idx} className="fireknife-reservation-info__item">
                                            <dt className="fireknife-reservation-info__dt">
                                                {getI18n(field.name, field.name_i18n)}
                                            </dt>
                                            <dd className="fireknife-reservation-info__dd">
                                                {field.type === 'dropdown'
                                                    ? getI18n(
                                                          field.value,
                                                          field.description_i18n,
                                                          field.description?.indexOf(field.value),
                                                      )
                                                    : field.value
                                                          .split('\n')
                                                          .map(
                                                              (line: string, lineIndex: number) => (
                                                                  <div key={lineIndex}>{line}</div>
                                                              ),
                                                          )}
                                            </dd>
                                        </div>
                                    ))}
                            </dl>
                        </div>
                    )}
                </div>
                {uniqueCustomFields.filter((field) => field.type === 'chat').length > 0 && (
                    <div className="fireknife-contact-and-other-data__box fireknife-contact-and-other-data__box--3">
                        <dl className="fireknife-reservation-info__dl">
                            <div className="fireknife-reservation-info__item">
                                <dt className="fireknife-reservation-info__dt">
                                    {t('widgetReservationInfo.notes')}
                                </dt>
                                <dd className="fireknife-reservation-info__dd">
                                    {uniqueCustomFields
                                        .filter((field) => field.type === 'chat')
                                        .map((field) => (
                                            <div key={field.name} style={{ marginBottom: '1em' }}>
                                                <i>{field?.name}</i>
                                                <br />
                                                {field.value}
                                            </div>
                                        ))}
                                </dd>
                            </div>
                        </dl>
                    </div>
                )}
            </div>
        </div>
    );
}
